import { Container } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Logo } from 'src/Components/Logo';
import { Props } from 'src/Components/Menu/Menu.types';
import { MenuItem } from 'src/Components/Menu/MenuItem';
import NavBar from 'src/Components/NavBar';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import { config, isMenuLink } from './Menu.config';
import styles from './Menu.module.scss';

const PERMISSION_LEVELS = ['none', 'read', 'own', 'full'] as const;

const hasNeededPermission = (
  neededPermission: { subject: string; minLevel?: 'read' | 'own' | 'full' } | undefined,
  isOwner: boolean | undefined,
  currentUserPermissions: object | undefined
): boolean => {
  if (!neededPermission) return true;
  if (isOwner) return true;

  const level = (currentUserPermissions as Record<string, string> | undefined)?.[
    neededPermission.subject
  ];
  const levelIndex = PERMISSION_LEVELS.indexOf(
    (level as (typeof PERMISSION_LEVELS)[number]) ?? 'none'
  );
  const minLevelIndex = PERMISSION_LEVELS.indexOf(neededPermission.minLevel ?? 'read');
  return levelIndex >= minLevelIndex;
};

const Menu = ({ toggleMenu, menuOpen, closeMenu }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();
  const { data: assetDetails } = useFetchAssetDetails();

  const menuConfig = useMemo(() => config(t), [t]);

  return (
    <div className={styles.container}>
      <Container className={styles.subContainer} hiddenFrom={'sm'}>
        <NavBar toggleMenu={toggleMenu} menuOpen={menuOpen} />
      </Container>

      <Link to={assetId ? `/asset/${assetId}/dashboard` : '/'} className={styles.logoRow}>
        <Logo variant="wordmark" className={styles.textLogo} />
      </Link>

      <div className={styles.tree}>
        {menuConfig
          .filter(
            config =>
              (config.neededRole === undefined && !config.forOwnerOnly) ||
              (assetDetails?.currentUserRole &&
                config.neededRole?.includes(assetDetails.currentUserRole)) ||
              (assetDetails?.isOwner && config.forOwnerOnly)
          )
          .filter(config =>
            hasNeededPermission(
              isMenuLink(config) ? config.neededPermission : undefined,
              assetDetails?.isOwner,
              assetDetails?.currentUserPermissions
            )
          )
          .map((item, index) => (
            <MenuItem item={item} onClick={closeMenu} key={[item.label, index].join('-')} />
          ))}
      </div>
    </div>
  );
};

export default Menu;
